import type { MigrationGroupsListData } from '../api/migrationGroups';
import type { StateName } from '../theme/tokens';

// The Home page's four tiles and its donut (FD-458).
//
// WHAT THE MOCKUP ASKS FOR AND WHY TWO OF ITS FIGURES ARE NOT REPRODUCED:
//
//   * "↑ 12% vs last 30 days" on every tile — nothing stores a previous value.
//     A delta would have to be invented, so there is no delta slot on MetricCard.
//   * "Devices by migration stage" across the whole deployment (Pre-Flight /
//     Enroute / Arrival / Completed) — computing it means reading every
//     campaign's device partition on a landing page. `GET /v1/overview` would
//     serve it in one read and is filed as an optional follow-on; until then the
//     donut charts CAMPAIGNS BY WAVE STATE, from `byBprtStatus`, which the
//     groups endpoint already returns. The title says so.
//
// Everything below comes from ONE existing call (`GET /v1/migration-groups`),
// whose rollup is computed server-side from rows it had already read.

export interface HomeDonutSegment {
  label: string;
  value: number;
  state: StateName;
}

/** Wave-state key → how it reads and which colour it earns. */
const WAVE_STATE_PRESENTATION: Record<string, { label: string; state: StateName }> = {
  active: { label: 'Active', state: 'progress' },
  revoked: { label: 'Completed', state: 'success' },
  expired: { label: 'Expired', state: 'warning' },
};

/**
 * Campaigns by wave state.
 *
 * `byBprtStatus` arrives PRE-SEEDED server-side but is open-ended, so this
 * iterates rather than enumerating: a state nobody anticipated is shown under
 * its own name in neutral instead of being dropped. The empty-string key is the
 * server's way of saying "never activated" and is labelled as such.
 */
export function campaignsByWaveState(rollup: MigrationGroupsListData['totals']): HomeDonutSegment[] {
  const segments: HomeDonutSegment[] = [];
  let notActivated = 0;

  for (const [key, count] of Object.entries(rollup.byBprtStatus ?? {})) {
    if (count <= 0) continue;
    const normalised = key.trim().toLowerCase();
    if (normalised === '' || normalised === 'null' || normalised === 'notactivated') {
      notActivated += count;
      continue;
    }
    const presentation = WAVE_STATE_PRESENTATION[normalised];
    segments.push({
      label: presentation?.label ?? key,
      value: count,
      state: presentation?.state ?? 'neutral',
    });
  }

  // A campaign with no wave state is the ordinary pre-activation case, so it is
  // counted rather than hidden — and it sorts last because it is the resting
  // state, not an outcome.
  const known = segments.reduce((sum, segment) => sum + segment.value, 0);
  const unaccounted = Math.max(0, rollup.migrationCount - known - notActivated);
  if (notActivated + unaccounted > 0) {
    segments.push({
      label: 'Not activated',
      value: notActivated + unaccounted,
      state: 'neutral',
    });
  }

  return segments;
}

/**
 * The identities tile is a CAPPED count, and the cap is visible.
 *
 * `GET /v1/identity-mappings` is a paginated cross-partition scan with no total
 * (FD-ADR-031 §5). Paging it to exhaustion on a landing page would be a scan per
 * visit, so the page stops after a bounded number of pages and this formats the
 * result honestly: "1,000+" rather than a number that silently means "as many as
 * we felt like counting".
 */
export function formatCappedCount(loaded: number, capped: boolean): string {
  return capped ? `${loaded.toLocaleString()}+` : loaded.toLocaleString();
}
